import { localizeSystemError } from '../i18n';
import { readAppLanguage } from '../i18n';
import { translationKey } from '../i18n';
import { localize } from '../utils/interfaceCopy';
import { copyText, shareText } from '../utils/shareText';
import useNavigate from '../hooks/useSafeNavigate';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import SettingsSheetTitle from '../components/ui/SettingsSheetTitle';
import InternationalText from '../components/ui/InternationalText';
import Button from '../components/ui/FitButton';
import FeatureUpdateDialog from '../components/FeatureUpdateDialog';
import { needsRelease105Update } from '../utils/release105Access';
import { journey } from '../utils/productJourneys';
import { isMedicationAlertTarget } from '../utils/medicationAlertTarget';
import { trackProduct } from '../utils/productAnalytics';
import Dialog from '../components/StableDialog';
import useRequestGuard from '../hooks/useRequestGuard';
import PageLoading from '../components/PageLoading';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, CircularProgress, IconButton, Tooltip, DialogContent, DialogContentText, DialogActions, Checkbox, FormControlLabel } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import googleLoginLogo from '../assets/google-login/g-logo.png';
import kakaoLoginArtwork from '../assets/kakao-login/login-large.png';
import { ReactComponent as AppleLoginLogo } from '../assets/sign-in-with-apple/apple-white.svg';
import PolicyIcon from '@mui/icons-material/Policy';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import AppToast from '../components/AppToast';
import AdPrivacyOptions from '../components/AdPrivacyOptions';
import AnalyticsPrivacyOptions from '../components/AnalyticsPrivacyOptions';
import BottomSheet from '../components/BottomSheet';
import AppPrivacySettingsSheet from '../components/AppPrivacySettingsSheet';
import { finishAccountDeletion } from '../utils/accountDeletion';
import { finishGoogleLogout } from '../utils/googleLogout';
import AccountDataChoiceDialog from '../components/AccountDataChoiceDialog';
import { FullScreenIntentPermissionBanner } from '../components/FullScreenIntentPermissionPrompt';
import NotificationPermissionBanner from '../components/NotificationPermissionBanner';
import IosRingtonePermissionBanner from '../components/IosRingtonePermissionBanner';
import AlertMethodSection from './medication-form/reminder/AlertMethodSection';
import WebviewMessenger from '../utils/WebviewMessenger';
import { shouldShowAppPrivacy, supportsAppPrivacy } from '../utils/appPrivacyAccess';
import { purchaseFailureMessage } from '../utils/purchaseFeedback';
import { getDeviceUuid, registerDevice, getSettings, updateSettings, getGroupInfo, createInviteCode, joinByInviteCode, disconnectGroupDevice, getGuardianTargets, getMyGuardians, deleteAllMyData, getEntitlements, getMedications, updateAllMedicationAlertMethod, getAccount, loginAccount, logoutAccount, deleteAccount, sendEmailLoginCode, verifyEmailLoginCode } from '../api';
import { getAppInstallText, INVITE_URL } from '../utils/storeLinks';
import { getAlertMethodSettingSubtitle, normalizeAlarmVolume, readAlertRuntimeInfo, readNativePlatform, supportsVibrationAlert } from '../utils/alertMethodSupport';
import { isDevClientPreview, supportsAccountFeatures, supportsAccountLogin } from '../utils/accountAccess';
import { clearAllScreenCaches, clearMedicationCaches } from '../utils/localCache';
import { applyPurchasedEntitlements, clearCachedEntitlements, getEntitlementRevision } from '../utils/adRequests';
import { syncWidget } from '../utils/widgetSync';
import { INDEFINITE_PAUSE_UNTIL } from '../utils/medicationPause';
import { formatPauseUntil } from '../utils/pauseDateCopy';
import useAppLanguage from '../i18n';

const loginButtonSx = {
    height: 48, minHeight: 48, minWidth: 140, borderRadius: '12px',
    px: '8%', py: 0, fontSize: '16px',
    letterSpacing: 0, lineHeight: 1.2, textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none',
};

function LoginButtonLabel({ icon, children }) {
    return <Box component="span" sx={{ display: 'grid', gridTemplateColumns: '31px minmax(0, 1fr)', columnGap: '6px', alignItems: 'center', width: 148, maxWidth: '100%', textAlign: 'left' }}>
        <Box component="span" aria-hidden="true" sx={{ width: 31, height: 44, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Box component="span">{children}</Box>
    </Box>;
}

function localToday() {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function isPauseActive(until) {
    return !!until && until >= localToday();
}

function formatConnectedAt(iso) {
    if (!iso) return localize("연결 시각을 알 수 없어요");
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return localize("연결 시각을 알 수 없어요");
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return localize("{v0}년 {v1}월 {v2}일 {v3}:{v4} 연결", { v0: year, v1: month, v2: day, v3: hours, v4: minutes });
}

function getDeviceDisplayName(device) {
    const model = (device?.deviceModel || '').trim();
    if (model) return model;
    if (device?.platform === 'ios') return 'iPhone';
    if (device?.platform === 'android') return localize("Android 기기");
    return localize("이름 없는 기기");
}

function getDevicePlatformLabel(device) {
    return device?.platform === 'ios' ? 'iOS' : device?.platform === 'android' ? 'Android' : localize("기기");
}

function getProviderLabel(provider) {
    if (provider === 'KAKAO') return localize("카카오");
    if (provider === 'APPLE') return 'Apple';
    if (provider === 'GOOGLE') return 'Google';
    if (provider === 'EMAIL') return localize("이메일");
    return localize("계정");
}

function SettingCell({ icon, title, subtitle, onClick, rightContent }) {
    const CellText = readAppLanguage() === 'ko' ? SingleLineText : Typography;
    return (
        <Box
            onClick={onClick}
            {...(onClick ? { role: 'button', tabIndex: 0, onKeyDown: event => {
                if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;
                event.preventDefault();
                if (!event.repeat) onClick(event);
            } } : {})}
            sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            py: 1.5, cursor: onClick ? 'pointer' : 'default',
            borderBottom: '1px solid #f5f5f5',
            '&:active': onClick ? { opacity: 0.6 } : {},
        }}>
            <Box sx={{
                width: 36, height: 36, borderRadius: '50%', bgcolor: '#f9fafb',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <CellText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a' }}>{title}</CellText>
                {subtitle && (
                    <CellText
                        sx={{
                            fontSize: '0.96rem',
                            color: '#777',
                            mt: 0.2,
                            whiteSpace: readAppLanguage() === 'ko' ? 'nowrap' : 'normal',
                        }}
                    >
                        {subtitle}
                    </CellText>
                )}
            </Box>
            {rightContent || (onClick && <ChevronRightIcon sx={{ color: '#888', fontSize: 20 }} />)}
        </Box>
    );
}

export default function SettingsPage() {
    const { language, t } = useAppLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cachedSettings = React.useMemo(() => {
        try { const r = sessionStorage.getItem('settings_cache'); return r ? JSON.parse(r) : null; } catch { return null; }
    }, []);
    const beginSettingsRequest = useRequestGuard('settings_cache');
    const [loading, setLoading] = useState(!cachedSettings);
    const [settingsLoadError, setSettingsLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [globalPause, setGlobalPause] = useState(isPauseActive(cachedSettings?.pauseUntil));
    const [pauseUntil, setPauseUntil] = useState(cachedSettings?.pauseUntil || '');
    const [pauseIndefinitely, setPauseIndefinitely] = useState(false);
    const pauseSavingRef = useRef(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [medicationCount, setMedicationCount] = useState(cachedSettings?.medicationCount || null);
    const [alertMethodSheetOpen, setAlertMethodSheetOpen] = useState(false);
    const [alertMethodForm, setAlertMethodForm] = useState({ alertType: 'PUSH', alarmVolume: 80 });
    const [savingAlertMethod, setSavingAlertMethod] = useState(false);
    const [alertRuntimeInfo, setAlertRuntimeInfo] = useState(() => readAlertRuntimeInfo());
    const [privacyUpdateOpen, setPrivacyUpdateOpen] = useState(false);
    const [privacySheetOpen, setPrivacySheetOpen] = useState(false);

    const [groupInfo, setGroupInfo] = useState(cachedSettings?.groupInfo || null);
    const [accountInfo, setAccountInfo] = useState(cachedSettings?.accountInfo || null);
    const [entitlements, setEntitlements] = useState(cachedSettings?.entitlements || null);
    const [guardianTargets, setGuardianTargets] = useState(cachedSettings?.guardianTargets || []);
    const [myGuardians, setMyGuardians] = useState([]);
    const [inviteCode, setInviteCode] = useState('');
    const [inviteType, setInviteType] = useState('SYNC'); // 'SYNC' | 'GUARDIAN'
    const [showDeviceLinkSheet, setShowDeviceLinkSheet] = useState(false);
    const [deviceLinkTab, setDeviceLinkTab] = useState('create');
    const [joinCode, setJoinCode] = useState('');
    const [joining, setJoining] = useState(false);
    const [disconnectingUuid, setDisconnectingUuid] = useState('');
    const [disconnectTarget, setDisconnectTarget] = useState(null);
    const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
    const [accountSheetOpen, setAccountSheetOpen] = useState(() => searchParams.get('account') === 'login');
    const [detachedChoiceOpen, setDetachedChoiceOpen] = useState(false);
    const [choiceData, setChoiceData] = useState(null);
    const pendingLoginRef = useRef(null);
    const privacyReturnUuidRef = useRef(null);
    const logoutBusyRef = useRef(false);
    const detachedChoiceRef = useRef(null);
    const accountLoginResultBusyRef = useRef(false);
    const accountLoginTimeoutRef = useRef(null);
    const finishDetachedChoice = (useAccount) => {
        setDetachedChoiceOpen(false);
        detachedChoiceRef.current?.(useAccount);
        detachedChoiceRef.current = null;
    };
    useEffect(() => () => {
        privacyReturnUuidRef.current = null;
        pendingLoginRef.current = null;
        detachedChoiceRef.current?.(null);
        window.clearTimeout(accountLoginTimeoutRef.current);
    }, []);
    const [accountLoginLoading, setAccountLoginLoading] = useState('');
    const [emailLoginOpen, setEmailLoginOpen] = useState(false);
    const [emailLoginEmail, setEmailLoginEmail] = useState('');
    const [emailLoginCode, setEmailLoginCode] = useState('');
    const [emailLoginCodeSent, setEmailLoginCodeSent] = useState(false);
    const [emailLoginPurpose, setEmailLoginPurpose] = useState('login');
    const emailProofRef = useRef(null);
    const emailSendBusyRef = useRef(false);
    const emailSendRequestRef = useRef(0);
    const [emailSending, setEmailSending] = useState(false);
    const [emailSendError, setEmailSendError] = useState('');
    const [emailAddressError, setEmailAddressError] = useState('');
    const emailResendDeadlinesRef = useRef(new Map());
    const emailExpiryDeadlinesRef = useRef(new Map());
    const [emailClock, setEmailClock] = useState(Date.now());
    const emailResendSeconds = Math.max(0, Math.ceil(((emailResendDeadlinesRef.current.get(emailLoginEmail.trim().toLowerCase()) || 0) - emailClock) / 1000));
    const emailExpiry = emailExpiryDeadlinesRef.current.get(emailLoginEmail.trim().toLowerCase());
    const emailValidSeconds = Math.max(0, Math.ceil(((emailExpiry || 0) - emailClock) / 1000));

    useEffect(() => {
        if (!emailLoginOpen) return;
        setEmailClock(Date.now());
        const timer = window.setInterval(() => setEmailClock(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [emailLoginOpen]);

    useEffect(() => {
        emailSendRequestRef.current += 1;
        emailSendBusyRef.current = false;
        setEmailSending(false);
        setEmailSendError('');
        setEmailAddressError('');
        if (emailLoginOpen) {
            const deadline = emailResendDeadlinesRef.current.get(emailLoginEmail.trim().toLowerCase()) || 0;
            setEmailLoginCodeSent(deadline > Date.now() - 540000);
        }
        return () => { emailSendRequestRef.current += 1; };
    }, [emailLoginOpen, emailLoginEmail, accountSheetOpen]);
    const [accountLogoutLoading, setAccountLogoutLoading] = useState(false);
    const [accountLogoutConfirmOpen, setAccountLogoutConfirmOpen] = useState(false);
    const [accountDeleteConfirmOpen, setAccountDeleteConfirmOpen] = useState(false);
    const [adFreeSheetOpen, setAdFreeSheetOpen] = useState(() => searchParams.get('adFree') === '1');
    const [adFreePurchaseLoading, setAdFreePurchaseLoading] = useState(false);
    const [adFreeRestoreLoading, setAdFreeRestoreLoading] = useState(false);
    const [adFreePurchaseError, setAdFreePurchaseError] = useState('');
    const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
    const [deleteStep, setDeleteStep] = useState(0); // 0: 닫힘, 1: 1차 확인, 2: 2차 확인
    const [deleting, setDeleting] = useState(false);
    const pendingAdFreeActionRef = useRef(null);
    const [adFreeStorePrice, setAdFreeStorePrice] = useState(null);
    const adFreeRequestSeqRef = useRef(0);
    useEffect(() => {
        if (localStorage.getItem('pill_reminder_installation_restored') === 'true') {
            if (supportsAccountFeatures(readAlertRuntimeInfo())) {
                setSnackbar({ open: true, severity: 'info', message: localize("새 기기로 확인됐어요. 약 정보는 로그인으로, 광고 제거는 로그인 또는 구매 복원으로 불러올 수 있어요.") });
            }
            localStorage.removeItem('pill_reminder_installation_restored');
        }
    }, []);


    const loadMedicationAlertSummary = async (isCurrent) => {
        const uuid = getDeviceUuid();
        const meds = await getMedications(uuid);
        if (!isCurrent()) return false;
        const scheduledMeds = meds.filter(med => isMedicationAlertTarget(med));
        setMedicationCount(scheduledMeds.length);
        const firstMed = scheduledMeds[0];
        if (firstMed) {
            const nextAlertType = firstMed.alertType || 'PUSH';
            setAlertMethodForm({
                alertType: nextAlertType === 'VIBRATION' && !supportsVibrationAlert() ? 'PUSH' : nextAlertType,
                alarmVolume: normalizeAlarmVolume(firstMed.alarmVolume),
            });
        }
        return true;
    };

    const [loadingAlertMethod, setLoadingAlertMethod] = useState(false);
    const alertMethodOpeningRef = useRef(false);
    const [creatingInvite, setCreatingInvite] = useState(false);
    const openAlertMethodSheet = async () => {
        if (alertMethodOpeningRef.current) return;
        alertMethodOpeningRef.current = true;
        setLoadingAlertMethod(true);
        const isCurrent = beginSettingsRequest('alert-method-opening');
        try {
            // Open once with the final selection, cards and action label together.
            // A loading placeholder inside the sheet changes its height after opening.
            if (await loadMedicationAlertSummary(isCurrent)) setAlertMethodSheetOpen(true);
        } catch {
            if (isCurrent()) setSnackbar({ open: true, message: localize("약 정보를 불러오지 못했어요"), severity: 'error' });
        } finally {
            alertMethodOpeningRef.current = false;
            setLoadingAlertMethod(false);
        }
    };

    const saveAllMedicationAlertMethod = async () => {
        setSavingAlertMethod(true);
        try {
            const uuid = getDeviceUuid();
            const canUseVibrationAlert = supportsVibrationAlert(alertRuntimeInfo, readNativePlatform(alertRuntimeInfo));
            const nextAlertType = alertMethodForm.alertType === 'VIBRATION' && !canUseVibrationAlert
                ? 'PUSH'
                : alertMethodForm.alertType;
            const updated = await updateAllMedicationAlertMethod(uuid, {
                alertType: nextAlertType,
                alarmVolume: nextAlertType === 'FULLSCREEN' ? normalizeAlarmVolume(alertMethodForm.alarmVolume) : null,
            });
            const scheduledUpdated = updated;
            setMedicationCount(scheduledUpdated.length);
            clearMedicationCaches();
            setAlertMethodSheetOpen(false);
            setSnackbar({
                open: true,
                message: scheduledUpdated.length > 0 ? localize("알림 대상 약의 알림을 바꿨어요") : localize("변경할 약이 없어요"),
                severity: scheduledUpdated.length > 0 ? 'success' : 'info',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || localize("알림 방식 저장에 실패했어요"),
                severity: 'error',
            });
        } finally {
            setSavingAlertMethod(false);
        }
    };

    const handleDeleteAllData = async () => {
        setDeleting(true);
        try {
            const uuid = getDeviceUuid();
            await deleteAllMyData(uuid);
            clearAllScreenCaches();
            // 모든 로컬 데이터 정리
            try { localStorage.clear(); } catch {}
            try { sessionStorage.clear(); } catch {}
            // 첫 진입 화면으로 (앱 재초기화 효과)
            window.location.replace('/');
        } catch (err) {
            setDeleting(false);
            setDeleteStep(0);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || localize("삭제에 실패했어요. 잠시 후 다시 시도해주세요."),
                severity: 'error',
            });
        }
    };

    const refreshAccountContext = async (authoritative, purchasedEntitlement) => {
        clearCachedEntitlements();
        const revision = getEntitlementRevision();
        clearAllScreenCaches({ identityChanged: Boolean(authoritative) });
        const isCurrent = beginSettingsRequest();
        if (authoritative) {
            setAccountInfo(authoritative);
            WebviewMessenger.send({ type: 'ENTITLEMENTS_CHANGED' });
        }
        const uuid = getDeviceUuid();
        try {
            const [account, group, entitlementData, targets, guardians, currentSettings] = await Promise.all([
                getAccount(uuid), getGroupInfo(uuid), getEntitlements(uuid), getGuardianTargets(uuid), getMyGuardians(uuid), getSettings(uuid).catch(() => null),
            ]);
            if (!isCurrent() || revision !== getEntitlementRevision() || uuid !== getDeviceUuid()) return account;
            applyPurchasedEntitlements(entitlementData);
            setAccountInfo(account);
            setGroupInfo(group);
            setEntitlements(entitlementData);
            setGuardianTargets(targets);
            setMyGuardians(guardians);
            setSettingsLoadError(!currentSettings);
            if (currentSettings) {
                const paused = isPauseActive(currentSettings.globalPausedUntil);
                setGlobalPause(paused);
                setPauseUntil(paused ? currentSettings.globalPausedUntil : '');
            }
            await syncWidget(uuid);
            return account;
        } catch (error) {
            if (isCurrent()) setSettingsLoadError(true);
            throw new Error(authoritative ? translationKey('연결 상태는 바뀌었지만 화면을 새로 불러오지 못했어요. 설정을 다시 열어주세요.') : translationKey('연결 상태를 확인하지 못했어요. 잠시 후 다시 시도해주세요.'));
        } finally {
            if (isCurrent()) setLoading(false);
        }
    };

    const requestAccountLogin = (provider, purpose = 'login') => {
        if (adFreePurchaseLoading || adFreeRestoreLoading) return;
        if (!supportsAccountLogin(readAlertRuntimeInfo())) return;
        if (provider === 'EMAIL') {
            if (purpose === 'delete' && !readAlertRuntimeInfo()?.capabilities?.accountDeletion) {
                setSnackbar({ open: true, message: localize("앱을 완전히 닫았다가 다시 열어 업데이트한 뒤 탈퇴해주세요."), severity: 'info' });
                return;
            }
            if (purpose !== 'delete') journey('account_login', 'start', 'email');
            emailProofRef.current = null;
            setAccountSheetOpen(true);
            setEmailLoginOpen(true);
            setEmailLoginPurpose(purpose);
            setEmailLoginCodeSent(false);
            setEmailLoginCode('');
            setAccountLoginLoading('');
            return;
        }
        if (!window.ReactNativeWebView) {
            setSnackbar({ open: true, message: localize("로그인은 앱에서 사용할 수 있어요"), severity: 'info' });
            return;
        }
        const runtimeInfo = readAlertRuntimeInfo();
        const supported = supportsAccountFeatures(runtimeInfo) && runtimeInfo?.capabilities?.accountLogin === true;
        if (!supported) {
            setSnackbar({ open: true, message: localize("앱 업데이트 후 다시 시도해주세요"), severity: 'info' });
            return;
        }
        if (pendingLoginRef.current || accountLoginResultBusyRef.current) return;
        if (purpose === 'delete' && !runtimeInfo?.capabilities?.accountDeletion) {
            setSnackbar({ open: true, message: localize("앱을 완전히 닫았다가 다시 열어 업데이트한 뒤 탈퇴해주세요."), severity: 'info' });
            return;
        }
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        pendingLoginRef.current = { requestId, provider, purpose, uuid: getDeviceUuid(), requiresId: runtimeInfo?.capabilities?.accountLoginRequestId === true };
        setAccountLoginLoading(provider);
        if (purpose !== 'delete') journey('account_login', 'start', provider.toLowerCase());
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_ACCOUNT_LOGIN', provider, requestId, purpose }));
        window.clearTimeout(accountLoginTimeoutRef.current);
        accountLoginTimeoutRef.current = window.setTimeout(() => {
            if (pendingLoginRef.current?.requestId !== requestId) return;
            pendingLoginRef.current = null;
            pendingAdFreeActionRef.current = null;
            setAccountLoginLoading('');
            setSnackbar({ open: true, message: localize("로그인 시간이 초과됐어요. 다시 시도해주세요."), severity: 'info' });
        }, 120000);
    };

    const handleAccountLogout = async () => {
        if (logoutBusyRef.current || adFreePurchaseLoading || adFreeRestoreLoading) return;
        logoutBusyRef.current = true;
        setAccountLogoutLoading(true);
        try {
            const uuid = getDeviceUuid();
            const provider = accountInfo?.provider;
            const result = await logoutAccount(uuid);
            const googleCleanup = provider === 'GOOGLE' ? await finishGoogleLogout(readAlertRuntimeInfo()) : null;
            await refreshAccountContext(result);
            const incomplete = googleCleanup === 'error' || googleCleanup === 'timeout';
            setSnackbar({ open: true, message: incomplete
                ? localize('앱에서는 로그아웃했어요. 다시 Google로 로그인할 때 계정을 확인해주세요.')
                : localize("이 기기에서 로그아웃했어요"), severity: incomplete ? 'info' : 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || localizeSystemError(err.message) || localize("로그아웃 결과를 확인하지 못했어요"), severity: 'error' });
        } finally {
            logoutBusyRef.current = false;
            setAccountLogoutLoading(false);
        }
    };

    useEffect(() => {
        if (adFreeSheetOpen && supportsAccountFeatures(alertRuntimeInfo) && alertRuntimeInfo?.capabilities?.adFreePurchase === true) {
            trackProduct('purchase_detail', { feature: 'ad_free', source: searchParams.get('adFree') === '1' ? 'home' : 'settings' });
            if (WebviewMessenger.isInWebView()) WebviewMessenger.send({ type: 'REQUEST_AD_FREE_PRODUCT' });
        }
    }, [adFreeSheetOpen, alertRuntimeInfo]);

    const startAdFreePurchase = () => {
        trackProduct('purchase_start', { feature: 'ad_free', source: 'settings' });
        setAdFreePurchaseError('');
        const requestSeq = adFreeRequestSeqRef.current + 1;
        adFreeRequestSeqRef.current = requestSeq;
        setAdFreePurchaseLoading(true);
        WebviewMessenger.send({ type: 'REQUEST_AD_FREE_PURCHASE' });
        window.setTimeout(() => {
            setAdFreePurchaseLoading((current) => {
                if (adFreeRequestSeqRef.current !== requestSeq) return current;
                if (!current) return current;
                setAdFreePurchaseError(translationKey('구매 확인이 늦어지고 있어요.\n이미 결제했다면 복원하기를 눌러주세요.'));
                setSnackbar({ open: true, message: localize("구매 확인이 늦어지고 있어요. 잠시 후 다시 확인해주세요."), severity: 'info' });
                return false;
            });
        }, 45000);
    };

    const startAdFreeRestore = () => {
        trackProduct('purchase_restore_start', { feature: 'ad_free', source: 'settings' });
        setAdFreePurchaseError('');
        const requestSeq = adFreeRequestSeqRef.current + 1;
        adFreeRequestSeqRef.current = requestSeq;
        setAdFreeRestoreLoading(true);
        WebviewMessenger.send({ type: 'REQUEST_AD_FREE_RESTORE' });
        window.setTimeout(() => {
            setAdFreeRestoreLoading((current) => {
                if (adFreeRequestSeqRef.current !== requestSeq) return current;
                if (!current) return current;
                setAdFreePurchaseError(translationKey('구매 복원을 확인하지 못했어요.\n잠시 후 복원하기를 다시 눌러주세요.'));
                setSnackbar({ open: true, message: localize("복원 확인이 늦어지고 있어요. 잠시 후 다시 확인해주세요."), severity: 'info' });
                return false;
            });
        }, 45000);
    };

    const handleAdFreePurchase = () => {
        if (!WebviewMessenger.isInWebView()) {
            setSnackbar({ open: true, message: localize("구매는 앱에서 사용할 수 있어요"), severity: 'info' });
            return;
        }
        if (!supportsAccountFeatures(alertRuntimeInfo) || !alertRuntimeInfo?.capabilities?.adFreePurchase) {
            setSnackbar({ open: true, message: localize("앱 업데이트 후 다시 시도해주세요"), severity: 'info' });
            return;
        }
        if (adFreePurchaseLoading || adFreeRestoreLoading || accountLoginLoading || accountLogoutLoading) return;
        startAdFreePurchase();
    };

    const handleAdFreeRestore = () => {
        if (!WebviewMessenger.isInWebView()) {
            setSnackbar({ open: true, message: localize("구매 복원은 앱에서 사용할 수 있어요"), severity: 'info' });
            return;
        }
        if (!supportsAccountFeatures(alertRuntimeInfo) || !alertRuntimeInfo?.capabilities?.adFreePurchase) {
            setSnackbar({ open: true, message: localize("앱 업데이트 후 다시 시도해주세요"), severity: 'info' });
            return;
        }
        if (adFreePurchaseLoading || adFreeRestoreLoading || accountLoginLoading || accountLogoutLoading) return;
        startAdFreeRestore();
    };

    const completeAccountLogin = async (loginRequest, pending) => {
        const uuid = getDeviceUuid();
        if (pending?.purpose === 'delete') {
            if (uuid !== pending.uuid) throw new Error(translationKey('기기 정보가 바뀌었어요. 다시 확인해주세요.'));
            const result = await deleteAccount(uuid, loginRequest);
            if (result.status !== 'deleted') throw new Error(translationKey('탈퇴 결과를 확인하지 못했어요. 다시 시도해주세요.'));
            clearAllScreenCaches();
            clearCachedEntitlements();
            await finishAccountDeletion();
            return;
        }
        let response = await loginAccount(uuid, loginRequest);
        while (response.dataChoice) {
            const selection = await new Promise(resolve => {
                detachedChoiceRef.current = resolve;
                setChoiceData(response.dataChoice);
                setDetachedChoiceOpen(true);
            });
            if (!selection) { journey('account_login', 'cancel', loginRequest.provider.toLowerCase()); pendingAdFreeActionRef.current = null; return; }
            journey('account_login', 'select', loginRequest.provider.toLowerCase());
            response = await loginAccount(uuid, { ...loginRequest, ...selection });
            if (response.dataChoice) setSnackbar({ open: true, message: localize("그동안 기록이 바뀌었어요. 최신 내용으로 다시 골라주세요."), severity: 'info' });
        }
        if (!response.linked) throw new Error(translationKey('로그인 결과를 확인하지 못했어요. 다시 시도해주세요.'));
        setAccountSheetOpen(false);
        setEmailLoginOpen(false);
        setEmailLoginCodeSent(false);
        setEmailLoginCode('');
        setEmailLoginPurpose('login');
        const account = await refreshAccountContext(response);
        if (privacyReturnUuidRef.current === uuid && getDeviceUuid() === uuid) {
            privacyReturnUuidRef.current = null;
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PRIVACY_ACCOUNT_READY', data: { uuid, email: loginRequest.provider === 'EMAIL' ? loginRequest.email : undefined } }));
        }
        const pendingAdFreeAction = pendingAdFreeActionRef.current;
        pendingAdFreeActionRef.current = null;
        if (pendingAdFreeAction === 'purchase' || pendingAdFreeAction === 'restore') {
            setAdFreeSheetOpen(true);
            setSnackbar({
                open: true,
                message: localize("로그인됐어요. 광고 제거를 이어갈게요"),
                severity: 'success',
            });
            window.setTimeout(() => {
                if (pendingAdFreeAction === 'purchase') startAdFreePurchase();
                else startAdFreeRestore();
            }, 900);
        } else {
            setSnackbar({
                open: true,
                message: localize("{v0} 계정에 연결됐어요", { v0: getProviderLabel(account?.provider || loginRequest.provider) }),
                severity: 'success',
            });
        }
    };

    const handleSendEmailLoginCode = async () => {
        if (!supportsAccountLogin(readAlertRuntimeInfo())) return;
        if (accountLoginLoading || emailSendBusyRef.current) return;
        const email = emailLoginEmail.trim();
        if (email.length < 5 || email.length > 255 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setEmailAddressError(translationKey('이메일 주소를 확인해주세요. 예: name@gmail.com'));
            return;
        }
        setEmailAddressError('');
        const emailKey = email.toLowerCase();
        if ((emailResendDeadlinesRef.current.get(emailKey) || 0) > Date.now()) return;
        emailResendDeadlinesRef.current.set(emailKey, Date.now() + 60000);
        emailExpiryDeadlinesRef.current.delete(emailKey);
        const requestedAt = Date.now();
        setEmailClock(Date.now());
        emailSendBusyRef.current = true;
        const requestId = ++emailSendRequestRef.current;
        emailProofRef.current = null;
        setEmailSending(true);
        setEmailSendError('');
        setEmailLoginCodeSent(true);
        setEmailLoginCode('');
        try {
            const uuid = getDeviceUuid();
            const response = await sendEmailLoginCode(uuid, email);
            if (requestId !== emailSendRequestRef.current) return;
            const completedAt = Date.now();
            emailResendDeadlinesRef.current.set(emailKey, completedAt + 60000);
            const ttl = response?.expiresInSeconds;
            emailExpiryDeadlinesRef.current.set(emailKey, Number.isFinite(ttl)
                ? completedAt + Math.max(0, ttl) * 1000 : requestedAt + 600000);
            setEmailClock(completedAt);
        } catch (err) {
            if (requestId !== emailSendRequestRef.current) return;
            setEmailSendError(err.response?.data?.message || translationKey('발송을 확인하지 못했어요. 메일이 없으면 다시 받아주세요.'));
        } finally {
            if (requestId === emailSendRequestRef.current) {
                emailSendBusyRef.current = false;
                setEmailSending(false);
            }
        }
    };

    const handleVerifyEmailLoginCode = async () => {
        if (!supportsAccountLogin(readAlertRuntimeInfo())) return;
        if (accountLoginLoading || accountLoginResultBusyRef.current || emailSendBusyRef.current || !/^\d{6}$/.test(emailLoginCode)) return;
        setAccountLoginLoading('EMAIL');
        accountLoginResultBusyRef.current = true;
        try {
            const uuid = getDeviceUuid();
            const proofKey = `${uuid}:${emailLoginPurpose}:${emailLoginEmail.trim().toLowerCase()}:${emailLoginCode}`;
            const verified = emailProofRef.current?.key === proofKey
                ? emailProofRef.current.value
                : await verifyEmailLoginCode(uuid, emailLoginEmail, emailLoginCode);
            emailProofRef.current = { key: proofKey, value: verified };
            await completeAccountLogin({
                dataProtocol: 2,
                provider: 'EMAIL',
                providerUserId: verified.providerUserId,
                email: verified.email || emailLoginEmail,
                loginToken: verified.loginToken,
            }, { purpose: emailLoginPurpose, uuid });
            emailProofRef.current = null;
        } catch (err) {
            if (err.response?.status >= 400 && err.response?.status < 500) emailProofRef.current = null;
            setSnackbar({ open: true, message: err.response?.data?.message || localizeSystemError(err.message) || localize("이메일 로그인에 실패했어요"), severity: 'error' });
        } finally {
            accountLoginResultBusyRef.current = false;
            setAccountLoginLoading('');
        }
    };

    // 초대 코드 생성 후 상대방 연결 감지 폴링
    useEffect(() => {
        if (!showDeviceLinkSheet || deviceLinkTab !== 'create' || !inviteCode) return;
        const initialCount = inviteType === 'GUARDIAN' ? guardianTargets.length : (groupInfo?.deviceCount || 1);
        const interval = setInterval(async () => {
            try {
                const uuid = getDeviceUuid();
                if (inviteType === 'GUARDIAN') {
                    const targets = await getGuardianTargets(uuid).catch(() => []);
                    if (targets.length > initialCount) {
                        setGuardianTargets(targets);
                        setShowDeviceLinkSheet(false);
                        setSnackbar({ open: true, message: localize("가족이 연결되었어요!"), severity: 'success' });
                        navigate('/guardian');
                    }
                } else {
                    const group = await getGroupInfo(uuid).catch(() => null);
                    if ((group?.deviceCount || 1) > initialCount) {
                        setGroupInfo(group);
                        setShowDeviceLinkSheet(false);
                        setSnackbar({ open: true, message: localize("기기가 연결되었어요!"), severity: 'success' });
                    }
                }
            } catch {}
        }, 3000);
        return () => clearInterval(interval);
    }, [showDeviceLinkSheet, deviceLinkTab, inviteCode]);

    useEffect(() => {
        const handler = (event) => setAlertRuntimeInfo(event?.detail || readAlertRuntimeInfo());
        window.addEventListener('app-runtime-info-updated', handler);
        return () => window.removeEventListener('app-runtime-info-updated', handler);
    }, []);

    useEffect(() => {
        const handler = async (event) => {
            let message;
            try {
                message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            } catch {
                return;
            }
            if (message?.type === 'OPEN_PRIVACY_ACCOUNT_LOGIN') { privacyReturnUuidRef.current = getDeviceUuid(); setAccountSheetOpen(true); return; }
            if (message?.type !== 'ACCOUNT_LOGIN_RESULT') return;
            const pending = pendingLoginRef.current;
            if (!pending || accountLoginResultBusyRef.current) return;
            if ((message.requestId ? message.requestId !== pending.requestId : pending.requiresId) || message.provider !== pending.provider) {
                if (!message.requestId && pending.requiresId) {
                    pendingLoginRef.current = null;
                    window.clearTimeout(accountLoginTimeoutRef.current);
                    pendingAdFreeActionRef.current = null;
                    setAccountLoginLoading('');
                    setSnackbar({ open: true, message: localize("앱을 완전히 닫았다가 다시 열어 업데이트한 뒤 로그인해주세요."), severity: 'info' });
                }
                return;
            }
            pendingLoginRef.current = null;
            window.clearTimeout(accountLoginTimeoutRef.current);
            if (message.status === 'cancelled') {
                if (pending.purpose !== 'delete') journey('account_login', 'cancel', pending.provider.toLowerCase());
                pendingAdFreeActionRef.current = null;
                setAccountLoginLoading('');
                return;
            }
            if (message.status !== 'success') {
                if (pending.purpose !== 'delete') journey('account_login', 'error', pending.provider.toLowerCase());
                pendingAdFreeActionRef.current = null;
                setAccountLoginLoading('');
                setSnackbar({ open: true, message: localize(message.message) || localize("로그인에 실패했어요"), severity: 'error' });
                return;
            }
            accountLoginResultBusyRef.current = true;
            try {
                const loginRequest = {
                    dataProtocol: 2,
                    provider: message.provider,
                    providerUserId: message.providerUserId,
                    accessToken: message.accessToken || null,
                    identityToken: message.identityToken || null,
                    authorizationCode: message.authorizationCode || null,
                };
                await completeAccountLogin(loginRequest, pending);
            } catch (err) {
                pendingAdFreeActionRef.current = null;
                clearAllScreenCaches();
                clearCachedEntitlements();
                setSnackbar({ open: true, message: err.response?.data?.message || localizeSystemError(err.message) || localize("연결 결과를 확인하지 못했어요. 다시 시도해주세요."), severity: 'error' });
            } finally {
                accountLoginResultBusyRef.current = false;
                setAccountLoginLoading('');
            }
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler);
        };
    }, []);

    useEffect(() => {
        return WebviewMessenger.onMessage(async (message) => {
            if (message?.type === 'AD_FREE_PRODUCT') {
                setAdFreeStorePrice(message.displayPrice || null);
                if (message.displayPrice) trackProduct('purchase_product_ready', { feature: 'ad_free', source: 'settings' });
                return;
            }
            if (message?.type !== 'AD_FREE_PURCHASE_RESULT') return;
            if (message.deviceUuid && message.deviceUuid !== getDeviceUuid()) return;
            if (message.silent) {
                if (message.status === 'success') await refreshAccountContext().catch(() => null);
                return;
            }
            adFreeRequestSeqRef.current += 1;
            setAdFreePurchaseLoading(false);
            setAdFreeRestoreLoading(false);
            if (message.status === 'cancelled') { trackProduct('purchase_cancel', { feature: 'ad_free', source: 'settings' }); return; }
            if (message.status === 'pending') {
                setAdFreePurchaseError(translationKey('스토어에서 결제 승인을 기다리고 있어요.\n승인이 완료되면 광고 제거가 적용돼요. 다시 구매하지 않아도 돼요.'));
                return;
            }
            if (message.status === 'success') {
                setAdFreePurchaseError('');
                clearAllScreenCaches();
                await refreshAccountContext().catch(() => null);
                setAdFreeSheetOpen(false);
                setSnackbar({
                    open: true,
                    message: localize(message.message) || (message.restored ? localize("구매 내역을 복원했어요") : localize("광고 제거가 적용됐어요")),
                    severity: 'success',
                });
                return;
            }
            trackProduct('purchase_error', { feature: 'ad_free', source: 'settings' });
            setAdFreePurchaseError(purchaseFailureMessage(message));
            setAdFreeSheetOpen(true);
            setSnackbar({
                open: true,
                message: message.status === 'not_found' ? localize("구매한 스토어 계정을 확인해주세요.") : localize("구매 확인이 어려워요. 아래 안내를 확인해주세요."),
                severity: message.status === 'not_found' ? 'info' : 'error',
            });
        });
    }, []);

    useEffect(() => {
        const isCurrent = beginSettingsRequest();
        (async () => {
            try {
                const uuid = getDeviceUuid();
                if (window.IS_NATIVE_APP) await registerDevice(uuid).catch(() => {});
                const [data, group, account, entitlementData, targets, guardians] = await Promise.all([
                    getSettings(uuid).catch(() => {
                        if (isCurrent()) setSettingsLoadError(true);
                        return cachedSettings?.settings || null;
                    }),
                    getGroupInfo(uuid).catch(() => null),
                    getAccount(uuid).catch(() => null),
                    getEntitlements(uuid).catch(() => null),
                    getGuardianTargets(uuid).catch(() => []),
                    getMyGuardians(uuid).catch(() => []),
                ]);
                if (!isCurrent()) return;
                setGroupInfo(group);
                setAccountInfo(account);
                setEntitlements(entitlementData);
                setGuardianTargets(targets); setMyGuardians(guardians);
                const gp = isPauseActive(data?.globalPausedUntil);
                const pu = data?.globalPausedUntil || '';
                setGlobalPause(gp); setPauseUntil(gp ? pu : '');
                try { sessionStorage.setItem('settings_cache', JSON.stringify({
                    settings: data, groupInfo: group, accountInfo: account, entitlements: entitlementData, guardianTargets: targets,
                    globalPause: gp, pauseUntil: pu,
                })); } catch {}
            } catch {} finally { if (isCurrent()) setLoading(false); }
        })();
    }, [cachedSettings, beginSettingsRequest]);

    const handleCreateInvite = async (type) => {
        if (creatingInvite) return;
        setCreatingInvite(true);
        try {
            const uuid = getDeviceUuid();
            if (window.IS_NATIVE_APP) await registerDevice(uuid).catch(() => {});
            const res = await createInviteCode(uuid, type);
            setInviteCode(res.inviteCode);
            setInviteType(type);
            try { void navigator.clipboard?.writeText(res.inviteCode)?.catch(() => {}); } catch {}
            setShowDeviceLinkSheet(true);
        } catch (err) {
            console.error('연결 코드 만들기 실패:', err?.response?.status, err?.response?.data, err?.message);
            setSnackbar({ open: true, message: localize("연결 코드 만들기 실패: {v0}", { v0: localizeSystemError(err?.response?.data?.message || err?.message || localize('알 수 없는 오류')) }), severity: 'error' });
        } finally { setCreatingInvite(false); }
    };

    const handleShare = async () => {
        const typeLabel = inviteType === 'GUARDIAN' ? translationKey('가족 약 챙기기') : translationKey('다른 기기 연결');
        const guide = inviteType === 'GUARDIAN'
            ? localize("앱에서 가족 약 챙기기 → 내가 가족 약을 챙길게요 → 위 코드를 입력해주세요.\n(5분 내 입력)\n\n초대 페이지: {v0}", { v0: INVITE_URL })
            : localize("앱에서 설정 → 다른 기기 연결에 위 코드를 입력해주세요.\n(5분 내 입력)");
        const message = localize("[집요한약알림] {v0} 초대\n\n연결 코드: {v1}\n\n{v2}\n\n앱이 없다면 아래 링크로 설치해주세요.\n{v3}", { v0: localize(typeLabel), v1: inviteCode, v2: guide, v3: getAppInstallText() });

        try {
            if (await shareText(message) === 'copied') {
                setSnackbar({ open: true, message: localize("공유 내용이 복사되었어요"), severity: 'success' });
            }
        } catch {
            setSnackbar({ open: true, message: localize("공유하지 못했어요. 연결 코드를 직접 전달해주세요"), severity: 'error' });
        }
    };

    const handleCopyCode = async () => {
        try {
            await copyText(inviteCode);
            setSnackbar({ open: true, message: localize("코드가 복사되었어요"), severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: localize("복사하지 못했어요. 화면의 코드를 직접 전달해주세요"), severity: 'error' });
        }
    };

    const handleJoin = async () => {
        if (joinCode.length !== 6) return;
        setJoining(true);
        try {
            const uuid = getDeviceUuid();
            const prevGuardiansCount = myGuardians.length;
            const prevTargetsCount = guardianTargets.length;
            await joinByInviteCode(uuid, joinCode);
            clearAllScreenCaches({ identityChanged: true });
            setShowDeviceLinkSheet(false); setJoinCode('');
            const [group, targets, guardians] = await Promise.all([
                getGroupInfo(uuid).catch(() => null),
                getGuardianTargets(uuid).catch(() => []),
                getMyGuardians(uuid).catch(() => []),
            ]);
            setGroupInfo(group);
            setGuardianTargets(targets);
            setMyGuardians(guardians);
            setSnackbar({ open: true, message: localize("기기가 연결되었어요!"), severity: 'success' });
            if (guardians.length > prevGuardiansCount) {
                navigate('/my-guardians');
            } else if (targets.length > prevTargetsCount) {
                navigate('/guardian');
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || localize("연결 실패. 코드를 확인해주세요."), severity: 'error' });
        } finally { setJoining(false); }
    };

    const handleDisconnectDevice = async () => {
        if (!disconnectTarget) return;
        setDisconnectingUuid(disconnectTarget.uuid);
        try {
            const uuid = getDeviceUuid();
            await disconnectGroupDevice(uuid, disconnectTarget.uuid);
            clearAllScreenCaches({ identityChanged: true });
            const group = await getGroupInfo(uuid).catch(() => null);
            setGroupInfo(group);
            setDisconnectTarget(null);
            setSnackbar({ open: true, message: localize("기기 연결을 끊었어요"), severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || localize("연결 해제에 실패했어요"), severity: 'error' });
        } finally {
            setDisconnectingUuid('');
        }
    };

    if (loading) {
        return <PageLoading />;
    }

    const canUseVibrationAlert = supportsVibrationAlert(alertRuntimeInfo, readNativePlatform(alertRuntimeInfo));
    const nativePlatform = readNativePlatform(alertRuntimeInfo);
    const isNativeApp = Boolean(window.IS_NATIVE_APP || window.ReactNativeWebView);
    const needsFeatureUpdate = needsRelease105Update(alertRuntimeInfo);
    const accountFeatureSupported = supportsAccountFeatures(alertRuntimeInfo);
    const accountLoginSupported = supportsAccountLogin(alertRuntimeInfo);
    const kakaoLoginSupported = accountLoginSupported && alertRuntimeInfo?.capabilities?.kakaoLogin === true;
    const googleLoginSupported = accountLoginSupported && alertRuntimeInfo?.capabilities?.googleLogin === true;
    const appleLoginSupported = accountLoginSupported && alertRuntimeInfo?.capabilities?.appleLogin === true;
    const appleDesignPreview = process.env.NODE_ENV === 'development' && alertRuntimeInfo?.isDev === true;
    const showAppleLogin = ((nativePlatform === 'ios' || !isNativeApp) && appleLoginSupported) || appleDesignPreview;
    const adFreePurchaseSupported = accountFeatureSupported
        && (alertRuntimeInfo?.capabilities?.adFreePurchase === true || isDevClientPreview(alertRuntimeInfo));
    const accountDeviceCount = accountInfo?.deviceCount || groupInfo?.deviceCount || 1;
    const accountConnectedDevices = Array.isArray(accountInfo?.devices) ? accountInfo.devices : [];
    const accountMenuSubtitle = accountInfo?.linked
        ? (accountDeviceCount > 1 ? localize("기기 {v0}대에서 동기화 중", { v0: accountDeviceCount }) : localize('약 정보 백업 중'))
        : localize('새 기기에서도 약 정보를 이어서 써요');
    const adFreeSubtitle = entitlements?.adFree
        ? localize('광고 없이 이용 중이에요')
        : localize('광고 없이 앱을 이용해요');
    const adFreeAccountPrice = adFreeStorePrice || localize('스토어에서 가격 확인');
    const displayAlertMethodForm = alertMethodForm.alertType === 'VIBRATION' && !canUseVibrationAlert
        ? { ...alertMethodForm, alertType: 'PUSH' }
        : alertMethodForm;

    return (
        <Box sx={{ pb: 10 }}>
            <PageHeader title={localize("설정")} matchContentHeight />
            <FeatureUpdateDialog platform={nativePlatform} open={privacyUpdateOpen || (needsFeatureUpdate && (accountSheetOpen || adFreeSheetOpen))}
                onClose={() => {
                    if (privacyUpdateOpen) setPrivacyUpdateOpen(false);
                    else { setAccountSheetOpen(false); setAdFreeSheetOpen(false); }
                }} />

            <Box sx={{ px: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5,
                '& > [role="status"]': { mb: 0 } }}>
                <NotificationPermissionBanner channelIds={['default', 'pill-reminder-alarm', 'pill-reminder-vibration', 'pill-alarm-foreground-v2']} />
                <FullScreenIntentPermissionBanner />
                <IosRingtonePermissionBanner onOpenAlertSettings={openAlertMethodSheet} />
                <Box data-settings-menu sx={{
                    '& > [role="button"]:first-of-type': { pt: 0 },
                    '& > [role="button"]:last-of-type': { pb: 0 },
                }}>
                    {(accountLoginSupported || needsFeatureUpdate) && (
                        <SettingCell
                            icon={<AccountCircleIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                            title={localize("백업 및 동기화")}
                            subtitle={accountMenuSubtitle}
                            onClick={() => setAccountSheetOpen(true)}
                        />
                    )}
                    <SettingCell
                        icon={<NotificationsActiveOutlinedIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={localize("약 알림 설정")}
                        subtitle={getAlertMethodSettingSubtitle(alertRuntimeInfo, readNativePlatform(alertRuntimeInfo))}
                        onClick={openAlertMethodSheet}
                        rightContent={loadingAlertMethod ? <CircularProgress size={20} aria-label={localize("알림 설정 불러오는 중")} /> : undefined}
                    />
                    {shouldShowAppPrivacy() && <SettingCell
                        icon={<LockOutlinedIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={localize("앱 잠금")}
                        subtitle={localize("비밀번호·생체 인증으로 앱을 잠가요")}
                        onClick={() => {
                            if (supportsAppPrivacy() && window.__APP_RUNTIME_INFO?.capabilities?.appPrivacyWebSettings) setPrivacySheetOpen(true);
                            else if (supportsAppPrivacy()) WebviewMessenger.send({ type: 'OPEN_PRIVACY_SETTINGS' });
                            else if (shouldShowAppPrivacy()) setPrivacyUpdateOpen(true);
                        }}
                    />}
                    <SettingCell
                        icon={<PauseCircleOutlineIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={settingsLoadError ? localize('중지 상태 확인 필요') : globalPause ? localize('복용 중지 중') : localize('복용 중지')}
                        subtitle={settingsLoadError ? localize('눌러서 다시 확인해 주세요') : globalPause
                            ? (pauseUntil === INDEFINITE_PAUSE_UNTIL ? localize('직접 다시 시작할 때까지 알림을 쉬어요') : pauseUntil ? localize("{v0}일까지 알림을 쉬어요", { v0: formatPauseUntil(pauseUntil) }) : localize('중지 기간을 정해 주세요'))
                            : localize('모든 약의 알림을 잠시 쉬어요')}
                        onClick={async () => {
                            if (settingsLoadError) {
                                const isCurrent = beginSettingsRequest();
                                try {
                                    const data = await getSettings(getDeviceUuid());
                                    if (!isCurrent()) return;
                                    setGlobalPause(isPauseActive(data.globalPausedUntil));
                                    setPauseUntil(isPauseActive(data.globalPausedUntil) ? data.globalPausedUntil : '');
                                    setSettingsLoadError(false);
                                } catch {
                                    if (!isCurrent()) return;
                                    setSnackbar({ open: true, message: localize('알림 설정을 불러오지 못했어요'), severity: 'error' });
                                    return;
                                }
                            }
                            setPauseIndefinitely(false);
                            setPauseConfirmOpen(true);
                        }}
                    />

                    {(adFreePurchaseSupported || needsFeatureUpdate) && (
                        <SettingCell
                            icon={<WorkspacePremiumIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={localize("광고 제거")}
                            subtitle={adFreeSubtitle}
                            onClick={() => setAdFreeSheetOpen(true)}
                        />
                    )}

                    {!accountInfo?.linked && <SettingCell
                        icon={<DeleteForeverIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={localize("내 데이터 모두 삭제")}
                        subtitle={localize("약 정보·복용 기록을 영구 삭제해요")}
                        onClick={() => setDeleteStep(1)}
                    />}

                    <SettingCell
                        icon={<PolicyIcon sx={{ color: '#8a7000', fontSize: 18 }} />}
                        title={localize("개인정보 처리방침")}
                        onClick={() => navigate('/privacy')}
                    />
                    <AnalyticsPrivacyOptions runtimeInfo={alertRuntimeInfo}
                        onError={message => setSnackbar({ open: true, message, severity: 'error' })} />
                    <AdPrivacyOptions runtimeInfo={alertRuntimeInfo}
                        onError={message => setSnackbar({ open: true, message, severity: 'error' })} />
                </Box>
            </Box>

            <Dialog dismissDisabled={Boolean(accountLoginLoading)} open={accountDeleteConfirmOpen} onClose={() => setAccountDeleteConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, pb: 0.5 }}>{t('계정을 탈퇴할까요?')}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '1.05rem', color: '#777', lineHeight: 1.7 }}>
                        {t('계정의 약 정보·복용 기록·보관 기록과 가족 연결이 삭제되고, 연결된 모든 기기에서 로그아웃돼요.')}
                        {' '}{t('삭제한 기록은 되돌릴 수 없어요.')}
                    </Typography>
                    <Typography sx={{ mt: 1.5, fontSize: '0.9rem', color: '#777', lineHeight: 1.6 }}>
                        {t('다른 가족의 기록과 로그아웃 후 따로 저장한 기록은 남아요.')}
                        {' '}{t('스토어 구매는 자동 환불되지 않아요. 구매한 스토어 계정으로 광고 제거를 다시 복원할 수 있어요.')}
                    </Typography>
                    <SingleLineText sx={{ mt: 1.5, fontSize: '0.9rem', color: '#777', ...(readAppLanguage() !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>
                        {t('현재 로그인된 계정으로 본인 확인 후 탈퇴해요.')}
                    </SingleLineText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setAccountDeleteConfirmOpen(false)} sx={{ flex: 1 }}>{t('아니요')}</Button>
                    <Button variant="contained" sx={{ flex: 1, bgcolor: '#ef4444', ...(readAppLanguage() !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }} onClick={() => {
                        setAccountDeleteConfirmOpen(false);
                        pendingAdFreeActionRef.current = null;
                        requestAccountLogin(accountInfo.provider, 'delete');
                    }}>{readAppLanguage() === 'ko' ? t('확인 후 탈퇴') : <Typography component="span" sx={{ font: 'inherit', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{t('확인 후 탈퇴')}</Typography>}</Button>
                </DialogActions>
            </Dialog>

            {detachedChoiceOpen && <AccountDataChoiceDialog key={choiceData?.revision} open data={choiceData} onFinish={finishDetachedChoice} />}
            <AppPrivacySettingsSheet open={privacySheetOpen} onClose={() => setPrivacySheetOpen(false)} />
            {/* 이어쓰기 BottomSheet */}
            <BottomSheet dismissDisabled={Boolean(accountLoginLoading || accountLogoutLoading || emailSending)} open={accountSheetOpen && accountLoginSupported}
                onBack={emailLoginOpen ? () => {
                    if (accountLoginLoading || accountLogoutLoading) return;
                    setEmailLoginOpen(false);
                    emailProofRef.current = null;
                    setEmailLoginPurpose('login');
                    setEmailLoginCodeSent(false);
                    setEmailLoginCode('');
                } : undefined} onClose={() => {
                if (!accountLoginLoading && !accountLogoutLoading) {
                    privacyReturnUuidRef.current = null;
                    pendingAdFreeActionRef.current = null;
                    setAccountSheetOpen(false);
                    setEmailLoginOpen(false);
                    setEmailLoginCodeSent(false);
                    setEmailLoginCode('');
                    setEmailLoginPurpose('login');
                    emailProofRef.current = null;
                }
            }}>
                <Box sx={{ position: 'relative' }}>
                    {emailLoginOpen && <Tooltip title={emailLoginPurpose === 'delete' ? localize("계정으로 돌아가기") : localize("다른 방법으로 로그인")}>
                        <span style={{ position: 'absolute', left: -8, top: -8 }}>
                            <IconButton aria-label={emailLoginPurpose === 'delete' ? localize("계정으로 돌아가기") : localize("다른 방법으로 로그인")}
                                disabled={Boolean(accountLoginLoading)}
                                onClick={() => {
                                    setEmailLoginOpen(false);
                                    emailProofRef.current = null;
                                    setEmailLoginPurpose('login');
                                    setEmailLoginCodeSent(false);
                                    setEmailLoginCode('');
                                }} sx={{ width: 44, height: 44, color: '#595959' }}>
                                <ArrowBackIcon />
                            </IconButton>
                        </span>
                    </Tooltip>}
                <SettingsSheetTitle sx={{ px: emailLoginOpen ? '36px' : 0 }}>
                    {emailLoginOpen ? (emailLoginPurpose === 'delete' ? localize('이메일로 본인 확인') : localize('이메일로 로그인')) : accountInfo?.linked ? localize('약 정보 백업 중') : localize('새 기기에서도 약 정보 그대로')}
                </SettingsSheetTitle>
                </Box>
                <Typography
                    sx={{
                    fontSize: '0.92rem',
                    color: emailLoginOpen ? '#595959' : '#777',
                    textAlign: 'center',
                    lineHeight: 1.55,
                    mb: 2.5,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'keep-all',
                }}>
                    {emailLoginOpen
                        ? (emailLoginCodeSent ? localize('이메일로 받은 번호를 입력해주세요') : emailLoginPurpose === 'delete' ? localize('로그인한 이메일을 입력해주세요') : localize('로그인할 이메일을 입력해주세요'))
                        : accountInfo?.linked ? localize('같은 계정으로 약 정보를 이어서 써요') : localize('로그인하면 새 기기에서도 약 정보를 불러와요')}
                </Typography>

                {emailLoginOpen ? (
                    <>
                        <TextField
                            fullWidth
                            type="email"
                            label={localize("이메일")}
                            error={Boolean(emailAddressError)}
                            helperText={localize(emailAddressError)}
                            InputLabelProps={{ shrink: true }}
                            value={emailLoginEmail}
                            onChange={(e) => {
                                setEmailLoginEmail(e.target.value);
                                setEmailAddressError('');
                                emailProofRef.current = null;
                                if (emailLoginCodeSent) {
                                    setEmailLoginCodeSent(false);
                                    setEmailLoginCode('');
                                }
                            }}
                            disabled={Boolean(accountLoginLoading)}
                            autoComplete="email"
                            inputProps={{ inputMode: 'email', 'data-keyboard-lift-action': 'true', maxLength: 255 }}
                            sx={{ mb: emailLoginCodeSent ? 2 : 0 }}
                        />
                        {emailLoginCodeSent && (
                            <TextField
                                fullWidth
                                label={localize("인증번호 6자리")}
                                InputLabelProps={{ shrink: true }}
                                value={emailLoginCode}
                                onChange={(e) => setEmailLoginCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                disabled={Boolean(accountLoginLoading)}
                                autoComplete="one-time-code"
                                inputProps={{ inputMode: 'numeric', 'data-keyboard-lift-action': 'true' }}
                                sx={{ mb: 0 }}
                            />
                        )}
                        {emailLoginCodeSent && (
                            <Typography sx={{ fontSize: '0.86rem', color: emailExpiry && !emailValidSeconds ? 'error.main' : '#595959', mt: 0.8 }}>
                                {emailSending ? localize("10분 안에 입력해주세요") : emailExpiry
                                    ? emailValidSeconds > 0
                                        ? localize("10분 안에 입력해주세요 · {v0}:{v1}", { v0: Math.floor(emailValidSeconds / 60), v1: String(emailValidSeconds % 60).padStart(2, '0') })
                                        : localize("인증번호가 만료됐어요. 다시 받아주세요.")
                                    : localize("10분 안에 입력해주세요")}
                            </Typography>
                        )}
                        {emailLoginCodeSent && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0 }}>
                                <Button variant="text" disabled={Boolean(accountLoginLoading) || emailSending || emailResendSeconds > 0}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={handleSendEmailLoginCode}
                                    sx={{ color: '#595959', fontSize: '0.9rem', minHeight: 44, px: 1 }}>
                                    {emailSending ? localize("인증번호 보내는 중…") : localize("인증번호 다시 받기")}
                                </Button>
                            </Box>
                        )}
                        {emailSendError && <Typography role="alert" sx={{ color: 'error.main', fontSize: '0.9rem', mb: 0, wordBreak: 'keep-all' }}>{localize(emailSendError)}</Typography>}
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={Boolean(accountLoginLoading) || emailSending || !emailLoginEmail.trim() || (!emailLoginCodeSent && emailResendSeconds > 0) || (emailLoginCodeSent && emailLoginCode.length !== 6)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={emailLoginCodeSent ? handleVerifyEmailLoginCode : handleSendEmailLoginCode}
                            sx={{
                                mt: 2.5, bgcolor: '#FEE500', color: '#191919', borderRadius: 3,
                                py: 1.45, minHeight: 54, fontWeight: 800, fontSize: '1.05rem',
                                boxShadow: 'none', '&:hover': { bgcolor: '#f2d900', boxShadow: 'none' },
                                '&.Mui-disabled': { bgcolor: '#f5eaa0', color: '#777' },
                            }}
                        >
                            {accountLoginLoading === 'EMAIL'
                                ? <CircularProgress size={22} sx={{ color: '#191919' }} />
                                : (emailLoginCodeSent ? (emailLoginPurpose === 'delete' ? localize("인증하고 탈퇴") : localize("인증하고 로그인")) : localize("인증번호 받기"))}
                        </Button>
                        <SingleLineText sx={{ fontSize: '0.94rem', color: '#595959', textAlign: 'center', lineHeight: 1.6, mt: 1.5, wordBreak: 'keep-all' }}>{localize("이메일은 계정 확인에만 사용해요.")}</SingleLineText>
                    </>
                ) : accountInfo?.linked ? (
                    <>
                        <Box sx={{ bgcolor: '#f9fafb', borderRadius: 3, border: '1px solid #e8e2c8', px: 2, py: 1.8, mb: 2.5 }}>
                            <SingleLineText sx={{ fontSize: '0.9rem', color: '#777', fontWeight: 700, mb: 0.5 }}>{localize("연결된 계정")}</SingleLineText>
                            <Typography sx={{ fontSize: '1.13rem', color: '#1a1a1a', fontWeight: 800 }}>
                                {getProviderLabel(accountInfo.provider)}{localize(" 로그인")}</Typography>
                            {accountInfo.emailHint && (
                                <Typography sx={{ fontSize: '0.9rem', color: '#666', overflowWrap: 'anywhere' }}>
                                    {accountInfo.emailHint}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: '0.95rem', color: '#666', mt: 0.7, lineHeight: 1.45 }}>
                                {accountDeviceCount > 1 ? localize("기기 {v0}대에서 같은 약 정보를 보고 있어요", { v0: accountDeviceCount }) : localize("이 기기의 약 정보와 기록을 백업하고 있어요")}
                            </Typography>
                        </Box>
                        {accountConnectedDevices.length > 0 && (
                            <Box sx={{ mb: 2.5 }}>
                                <SingleLineText sx={{ fontSize: '0.9rem', color: '#555', fontWeight: 800, mb: 1 }}>{localize("연결된 기기")}</SingleLineText>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                    {accountConnectedDevices.map((device) => (
                                        <Box
                                            key={device.uuid}
                                            sx={{
                                                borderRadius: 2.5,
                                                border: '1px solid #eef2f7',
                                                bgcolor: '#fff',
                                                px: 1.35,
                                                py: 1.15,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.1,
                                            }}
                                        >
                                            <Box sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: '50%',
                                                bgcolor: '#f8fafc',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <PhonelinkIcon sx={{ fontSize: 19, color: '#8a7000' }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                                                    <Typography noWrap sx={{ fontSize: '0.96rem', fontWeight: 800, color: '#1a1a1a', minWidth: 0 }}>
                                                        {getDeviceDisplayName(device)}
                                                    </Typography>
                                                    {device.isCurrentDevice && (
                                                        <Typography component="span" sx={{
                                                            flexShrink: 0,
                                                            fontSize: '0.72rem',
                                                            lineHeight: 1,
                                                            fontWeight: 800,
                                                            color: '#7c6a00',
                                                            bgcolor: '#fff7cc',
                                                            borderRadius: 999,
                                                            px: 0.8,
                                                            py: 0.45,
                                                        }}>{localize("이 기기")}</Typography>
                                                    )}
                                                </Box>
                                                <Typography noWrap sx={{ fontSize: '0.8rem', color: '#888', mt: 0.35 }}>
                                                    {getDevicePlatformLabel(device)} · {formatConnectedAt(device.connectedAt)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Button fullWidth variant="contained" disabled={accountLogoutLoading || Boolean(accountLoginLoading)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setAccountLogoutConfirmOpen(true)}
                            sx={{
                                bgcolor: '#ef4444',
                                color: '#fff',
                                borderRadius: 3,
                                py: 1.35, fontWeight: 800, fontSize: '1rem',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                                '&:active': { bgcolor: '#b91c1c' },
                                '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' },
                            }}>
                            {accountLogoutLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : localize("이 기기에서 로그아웃")}
                        </Button>
                        <SingleLineText sx={{ fontSize: '0.86rem', color: '#999', textAlign: 'center', lineHeight: 1.45, mt: 1.2, ...(readAppLanguage() !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>{localize("로그아웃해도 이 기기 데이터는 남아요.")}</SingleLineText>
                        <Button fullWidth variant="text" disabled={accountLogoutLoading || Boolean(accountLoginLoading)}
                            onClick={() => setAccountDeleteConfirmOpen(true)}
                            onMouseDown={e => e.preventDefault()}
                            sx={{ mt: 1, color: '#777', fontSize: '0.9rem', minHeight: 44 }}>
                            {accountLoginLoading ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />{localize("계정 확인 및 처리 중…")}</> : localize("계정 탈퇴")}
                        </Button>
                    </>
                ) : (
                    <>
                        {entitlements?.purchaseLinkStatus === 'UNLINKED' && <SingleLineText sx={{ mb: 1.5, fontSize: '0.9rem', textAlign: 'center', wordBreak: 'keep-all' }}>{localize("이 기기의 광고 제거 구매도 계정에 연결돼요.")}</SingleLineText>}
                        {kakaoLoginSupported && (
                            <Button variant="contained" fullWidth disabled={Boolean(accountLoginLoading)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => requestAccountLogin('KAKAO')}
                                sx={{
                                    ...loginButtonSx,
                                    bgcolor: '#FEE500', color: '#191919',
                                    boxShadow: 'none', '&:hover': { bgcolor: '#FEE500', boxShadow: 'none' },
                                    '&.Mui-disabled': { bgcolor: '#FEE500', color: '#191919', opacity: 0.55 },
                                }}>
                                {accountLoginLoading === 'KAKAO' ? <CircularProgress size={22} sx={{ color: '#191919' }} /> : <LoginButtonLabel icon={
                                    <Box component="span" sx={{ width: 20, height: 20, ml: '7.75px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                                        <Box component="img" src={kakaoLoginArtwork} alt="" sx={{ position: 'absolute', width: 90, height: 45, maxWidth: 'none', left: -14, top: -13 }} />
                                    </Box>
                                }>{localize("카카오로 로그인")}</LoginButtonLabel>}
                            </Button>
                        )}
                        {showAppleLogin && (
                            <Button variant="contained" fullWidth disabled={Boolean(accountLoginLoading)} aria-label={localize("Apple로 로그인")}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    if (!appleLoginSupported || nativePlatform !== 'ios') {
                                        setSnackbar({ open: true, message: localize("디자인 미리보기예요. Apple 로그인은 iPhone에서 사용할 수 있어요."), severity: 'info' });
                                        return;
                                    }
                                    requestAccountLogin('APPLE');
                                }}
                                sx={{
                                    ...loginButtonSx,
                                    mt: kakaoLoginSupported ? 1.25 : 0,
                                    bgcolor: '#000', color: '#fff',
                                    boxShadow: 'none', '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                                    '&.Mui-disabled': { bgcolor: '#000', color: '#fff', opacity: 0.55 },
                                }}>
                                {accountLoginLoading === 'APPLE' ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : <LoginButtonLabel icon={
                                    <AppleLoginLogo aria-hidden="true" style={{ height: 44, width: 31, flexShrink: 0 }} />
                                }>{localize("Apple로 로그인")}</LoginButtonLabel>}
                            </Button>
                        )}
                        {googleLoginSupported && (
                            <Button variant="contained" fullWidth disabled={Boolean(accountLoginLoading)}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => requestAccountLogin('GOOGLE')}
                                sx={{
                                    ...loginButtonSx, mt: (kakaoLoginSupported || showAppleLogin) ? 1.25 : 0,
                                    bgcolor: '#F2F2F2', color: '#1F1F1F', border: 0, fontWeight: 500,
                                    '&:hover': { bgcolor: '#E8EAED', boxShadow: 'none' },
                                    '&.Mui-disabled': { bgcolor: '#F2F2F2', color: '#1F1F1F', opacity: 0.55 },
                                }}>
                                {accountLoginLoading === 'GOOGLE' ? <CircularProgress size={22} sx={{ color: '#1F1F1F' }} /> : <LoginButtonLabel icon={<Box component="img" src={googleLoginLogo} alt="" sx={{ width: 20, height: 20, ml: '7.75px', flexShrink: 0 }} />}>{localize("Google로 로그인")}</LoginButtonLabel>}
                            </Button>
                        )}
                        <Button variant="text" fullWidth disabled={Boolean(accountLoginLoading)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setEmailLoginOpen(true)}
                            sx={{
                                ...loginButtonSx,
                                mt: (kakaoLoginSupported || googleLoginSupported || showAppleLogin) ? 1.25 : 0,
                                color: '#345A86', bgcolor: '#EAF2FF',
                                '&:hover': { bgcolor: '#DFEBFF' },
                                '&.Mui-disabled': { bgcolor: '#EAF2FF', color: '#345A86', opacity: 0.55 },
                            }}>
                            <LoginButtonLabel icon={<MailOutlineIcon sx={{ fontSize: 20, ml: '7.75px', flexShrink: 0 }} />}>{localize("이메일로 로그인")}</LoginButtonLabel>
                        </Button>
                        <SingleLineText sx={{ fontSize: '0.88rem', color: '#777', textAlign: 'center', lineHeight: 1.5, mt: 2, ...(readAppLanguage() === 'ko' ? {} : { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>{localize("로그인 정보는 계정 확인에만 사용해요.")}</SingleLineText>
                    </>
                )}
            </BottomSheet>

            <Dialog
                open={accountLogoutConfirmOpen}
                onClose={() => { if (!accountLogoutLoading) setAccountLogoutConfirmOpen(false); }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}
            >
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>{localize("이 기기에서 로그아웃할까요?")}</DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '0.95rem', color: '#666', lineHeight: 1.7, wordBreak: 'keep-all' }}>{localize("약과 기록은 이 기기에 남아요.")}</DialogContentText>
                    <Box>
                        <InternationalText sx={{ fontSize: '0.95rem', color: '#666', lineHeight: 1.7, wordBreak: 'keep-all' }}>{localize("이 기기의 가족 약 알림은 멈춰요.")}</InternationalText>
                        <Typography sx={{ mt: 1.5, fontSize: '0.875rem', color: '#888', lineHeight: 1.65, wordBreak: 'keep-all' }}>{localize("가족 연결은 계정에 보관돼요.")}<br />{localize("같은 계정으로 로그인하면 이어져요.")}</Typography>
                    </Box>
                {entitlements?.adFree && <Typography sx={{ mt: 1, color: '#777', wordBreak: 'keep-all' }}>
                    {entitlements.deviceAdFree ? localize("로그아웃해도 이 기기의 광고 제거는 유지돼요.") : localize("로그아웃하면 이 기기의 광고 제거 혜택이 종료돼요. 같은 계정으로 다시 로그인하면 이어서 쓸 수 있어요.")}
                </Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setAccountLogoutConfirmOpen(false)}
                        disabled={accountLogoutLoading}
                        variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            bgcolor: '#f5f5f5',
                            '&:hover': { bgcolor: '#ececec' },
                        }}
                    >{localize("아니요")}</Button>
                    <Button
                        onClick={async () => {
                            await handleAccountLogout();
                            setAccountLogoutConfirmOpen(false);
                        }}
                        disabled={accountLogoutLoading}
                        variant="contained"
                        sx={{
                            bgcolor: '#ef4444', color: '#fff', fontWeight: 700,
                            fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' },
                        }}
                    >
                        {accountLogoutLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : localize("로그아웃하기")}
                    </Button>
                </DialogActions>
            </Dialog>

            <BottomSheet dismissDisabled={adFreePurchaseLoading || adFreeRestoreLoading} open={adFreeSheetOpen && adFreePurchaseSupported} onClose={() => setAdFreeSheetOpen(false)}>
                <Box sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    bgcolor: entitlements?.adFree ? '#dcfce7' : '#fff2bd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.2,
                }}>
                    <WorkspacePremiumIcon sx={{ color: entitlements?.adFree ? '#16a34a' : '#8a7000', fontSize: 24 }} />
                </Box>
                <SettingsSheetTitle>
                    {entitlements?.adFree ? localize("모든 광고 제거 사용 중") : localize("모든 광고 제거")}
                </SettingsSheetTitle>

                <Typography
                    sx={{
                        fontSize: '0.92rem',
                        color: '#777',
                        textAlign: 'center',
                        lineHeight: 1.55,
                        mb: 2,
                        whiteSpace: 'pre-line',
                        wordBreak: 'keep-all',
                    }}
                >
                    {entitlements?.adFree
                        ? localize("모든 광고 없이 이용하고 있어요.")
                        : localize("광고 없이, 더 여유롭게 약을 챙겨요.")}
                </Typography>

                <Box sx={{ display: 'grid', gap: 1.2, width: 'fit-content', maxWidth: '100%', mx: 'auto', mb: 2.5 }}>
                    {[localize('복용 처리 후 광고 없이'), localize('약 등록 개수 무제한'), localize('AI 상담 하루 최대 100회')].map(benefit => (
                        <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Typography aria-hidden="true" sx={{ color: '#8a7000', fontSize: 18, fontWeight: 800 }}>✓</Typography>
                            <SingleLineText sx={{ fontSize: '1rem', fontWeight: 700, color: '#191f28' }}>{benefit}</SingleLineText>
                        </Box>
                    ))}
                </Box>

                {entitlements?.adFree ? (
                    <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, px: 2, py: 1.8 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: '#15803d', lineHeight: 1.45, textAlign: 'center', wordBreak: 'keep-all' }}>
                            {!(entitlements?.accountAdFree ?? entitlements?.scope === 'GROUP')
                                ? localize("이 휴대폰에 적용되어 있어요.")
                                : <>{localize("휴대폰을 바꿔도 같은 계정으로 로그인하면")}<br />{localize("유지돼요.")}</>}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {!alertRuntimeInfo?.capabilities?.adFreePurchase && isDevClientPreview(alertRuntimeInfo) && (
                            <Typography sx={{ color: '#777', textAlign: 'center', mb: 1.5 }}>{localize("dev client에서는 화면만 확인할 수 있어요. 실제 결제와 복원은 스토어 빌드에서 테스트해주세요.")}</Typography>
                        )}
                        <Box
                            sx={{
                                border: '1px solid #ececec',
                                borderRadius: 2,
                                bgcolor: '#fff',
                                px: 2,
                                py: 1.7,
                                boxShadow: '0 6px 18px rgba(0,0,0,0.04)',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ minWidth: 0, flex: '1 1 180px' }}>
                                    <SingleLineText sx={{ fontSize: '1.05rem', color: '#1a1a1a', fontWeight: 800 }}>{localize("한 번 구매로 계속 이용")}</SingleLineText>
                                    <SingleLineText sx={{ fontSize: '0.92rem', color: '#777', mt: 0.4, lineHeight: 1.45 }}>{localize("정기 결제 없이 이용해요.")}</SingleLineText>
                                </Box>
                                <Typography sx={{ fontSize: '1rem', color: '#8a7000', fontWeight: 900, flexShrink: 0 }}>
                                    {adFreeAccountPrice}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography sx={{ mt: 1.2, textAlign: 'center', color: '#777', fontSize: '0.86rem' }}>{localize("로그인 없이 구매할 수 있어요.")}</Typography>
                        {adFreePurchaseError && (
                            <Typography role="alert" sx={{ color: '#b91c1c', mt: 1.5, fontSize: '0.95rem', lineHeight: 1.5, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
                                {localize(adFreePurchaseError)}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={adFreePurchaseLoading || adFreeRestoreLoading || !alertRuntimeInfo?.capabilities?.adFreePurchase}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleAdFreePurchase}
                            sx={{
                                mt: 2.5,
                                bgcolor: '#ffe566',
                                color: '#5a4a00',
                                borderRadius: 3,
                                height: 52,
                                py: 0,
                                fontWeight: 800,
                                fontSize: '1.02rem',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                '&.Mui-disabled': { bgcolor: '#f5f5f5', color: '#aaa' },
                            }}
                        >
                            {adFreePurchaseLoading ? <CircularProgress size={22} sx={{ color: '#5a4a00' }} /> : localize("광고 제거 구매하기")}
                        </Button>
                    </>
                )}
                {(!entitlements?.adFree || entitlements?.purchaseLinkStatus === 'RESTORE_TO_LINK') && (
                    <Button
                        variant="text"
                        fullWidth
                        disabled={adFreePurchaseLoading || adFreeRestoreLoading || !alertRuntimeInfo?.capabilities?.adFreePurchase}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleAdFreeRestore}
                        sx={{
                            mt: 1,
                            height: 52,
                            py: 0,
                            bgcolor: '#f5f5f5',
                            border: 'none',
                            color: '#666',
                            borderRadius: 3,
                            fontWeight: 800,
                            fontSize: '1.02rem',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#eeeeee', border: 'none', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#f5f5f5', color: '#aaa' },
                        }}
                    >
                        {adFreeRestoreLoading ? <CircularProgress size={22} sx={{ color: '#666' }} /> : localize("구매 복원하기")}
                    </Button>
                )}
                {!entitlements?.adFree && (
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 400, color: '#777', textAlign: 'center', lineHeight: 1.55, wordBreak: 'keep-all', mt: 1.5 }}>{localize("결제는 {store}에서 안전하게 진행돼요.", { store: nativePlatform === 'ios' ? 'App Store' : nativePlatform === 'android' ? 'Google Play' : localize("앱 스토어") })}</Typography>
                )}
                {entitlements?.purchaseLinkStatus === 'TEST_DEVICE_ONLY' && <SingleLineText sx={{ mt: 1, fontSize: '0.9rem', wordBreak: 'keep-all' }}>{localize("테스트 구매가 이 기기에 적용되어 있어요.")}</SingleLineText>}
                {entitlements?.purchaseLinkStatus === 'OTHER_ACCOUNT' && (
                    <Typography sx={{ mt: 1.5, fontSize: '0.9rem', wordBreak: 'keep-all' }}>{localize("이 구매는 다른 앱 계정에 연결되어 있어요. 이 기기에서는 계속 이용할 수 있어요.")}</Typography>
                )}
                {entitlements?.purchaseLinkStatus === 'RESTORE_TO_LINK' && (
                    <Typography sx={{ mt: 1.5, fontSize: '0.9rem', wordBreak: 'keep-all' }}>
                        {accountInfo?.linked ? localize("현재 계정에도 연결하려면 구매 복원을 눌러주세요.") : localize("계정에 연결하기 전에 구매 복원을 눌러주세요.")}
                    </Typography>
                )}
                {entitlements?.adFree && adFreePurchaseError && <Typography role="alert" sx={{ color: '#b91c1c', mt: 1 }}>{localize(adFreePurchaseError)}</Typography>}
                {!accountInfo?.linked && entitlements?.purchaseLinkStatus !== 'TEST_DEVICE_ONLY' && <Typography sx={{ fontSize: '0.86rem', fontWeight: 400, color: '#777', textAlign: 'center', lineHeight: 1.55, wordBreak: 'keep-all' }}>
                    {entitlements?.purchaseLinkStatus === 'CONNECTED_ACCOUNT'
                        ? localize("구매를 연결한 계정으로 로그인하면 다른 기기에서도 이용할 수 있어요.")
                        : localize("로그인하면 같은 계정의 다른 기기에서도 이용할 수 있어요.")}
                </Typography>}
            </BottomSheet>


            {/* 다른 기기 연결 BottomSheet */}
            <BottomSheet dismissDisabled={joining || creatingInvite} open={showDeviceLinkSheet} onClose={() => { setShowDeviceLinkSheet(false); setJoinCode(''); setInviteCode(''); }}>
                <SettingsSheetTitle sx={{ mb: 2 }}>{localize(translationKey("다른 기기 연결"))}</SettingsSheetTitle>

                {/* 탭 */}
                <Box sx={{ display: 'flex', bgcolor: '#f3f4f6', borderRadius: 2.5, p: 0.5, mb: 2.5 }}>
                    {[{ key: 'create', label: translationKey('코드 생성') }, { key: 'join', label: translationKey('코드 입력') }].map(({ key, label }) => (
                        <Box key={key} role="button" tabIndex={0} onClick={() => { setDeviceLinkTab(key); if (key === 'join') setInviteCode(''); }}
                            sx={{
                                flex: 1, py: 1, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
                                bgcolor: deviceLinkTab === key ? '#fff' : 'transparent',
                                boxShadow: deviceLinkTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.15s ease',
                            }}>
                            <SingleLineText sx={{
                                fontSize: '1rem', fontWeight: deviceLinkTab === key ? 700 : 500,
                                color: deviceLinkTab === key ? '#1a1a1a' : '#999',
                            }}>{localize(label)}</SingleLineText>
                        </Box>
                    ))}
                </Box>

                {deviceLinkTab === 'create' ? (
                    inviteCode ? (
                        <>
                            <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mb: 2, textAlign: 'center' }}>{localize("다른 폰에서 이 코드를 입력해주세요")}</SingleLineText>
                            <Box sx={{ bgcolor: '#f9fafb', borderRadius: 4, py: 2.5, mb: 1.5, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '2.2rem', fontWeight: 800, color: '#1a1a1a', letterSpacing: '0.3em' }}>
                                    {inviteCode}
                                </Typography>
                            </Box>
                            <SingleLineText sx={{ fontSize: '0.95rem', color: '#999', textAlign: 'center', mb: 2.5 }}>{localize("5분 내에 입력해야 해요")}</SingleLineText>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" fullWidth startIcon={<ContentCopyIcon />}
                                    onMouseDown={(e) => e.preventDefault()} onClick={handleCopyCode}
                                    sx={{ borderColor: '#e5e7eb', color: '#333', borderRadius: 3, py: 1.2, fontWeight: 700 }}>{localize("복사")}</Button>
                                <Button variant="contained" fullWidth
                                    onMouseDown={(e) => e.preventDefault()} onClick={handleShare}
                                    sx={{ bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3, py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#e8d040' } }}>{localize("공유하기")}</Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            {groupInfo?.deviceCount > 1 && (
                                <Box sx={{ mb: 2.5 }}>
                                    <SingleLineText sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#333', mb: 1.2 }}>{localize("연결된 기기")}</SingleLineText>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {groupInfo.devices.filter((device) => !device.isCurrentDevice).map((device) => (
                                            <Box
                                                key={device.uuid}
                                                sx={{
                                                    borderRadius: 3,
                                                    bgcolor: '#f9fafb',
                                                    border: '1px solid #eef2f7',
                                                    px: 1.5,
                                                    py: 1.35,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: 0.4 }}>
                                                    <Typography sx={{ fontSize: '0.97rem', fontWeight: 800, color: '#1a1a1a' }}>
                                                        {getDeviceDisplayName(device)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#7c6a00', fontWeight: 700 }}>
                                                        {getDevicePlatformLabel(device)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: '0.83rem', color: '#666', lineHeight: 1.45 }}>
                                                    {formatConnectedAt(device.connectedAt)}
                                                </Typography>
                                                <Button
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => { setDisconnectTarget(device); setDisconnectConfirmOpen(true); }}
                                                    startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        mt: 1.15,
                                                        minHeight: 34,
                                                        px: 1.4,
                                                        borderRadius: 999,
                                                        bgcolor: '#fff5f5',
                                                        color: '#c24141',
                                                        fontSize: '0.82rem',
                                                        fontWeight: 800,
                                                        alignSelf: 'flex-start',
                                                        border: '1px solid #ffd9d9',
                                                        '&:hover': { bgcolor: '#ffeaea', borderColor: '#ffc7c7' },
                                                    }}
                                                >{localize("연결 끊기")}</Button>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                                <Box sx={{
                                    width: 56, height: 56, borderRadius: '50%', bgcolor: '#fffbeb',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                                }}>
                                    <PhonelinkIcon sx={{ fontSize: 28, color: '#8a7000' }} />
                                </Box>
                                <Typography sx={{ fontSize: '1.05rem', color: '#777', lineHeight: 1.6 }}>
                                    {groupInfo?.deviceCount > 1
                                        ? localize("새 기기를 더 연결하려면 아래에서 코드를 만드세요")
                                        : localize("같은 약 정보를 다른 폰에서도\n함께 볼 수 있어요")}
                                </Typography>
                            </Box>
                            <Button variant="contained" fullWidth
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={creatingInvite}
                                onClick={() => handleCreateInvite('SYNC')}
                                sx={{
                                    bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                                    py: 1.5, fontWeight: 700, fontSize: '1.1rem',
                                    '&:hover': { bgcolor: '#e8d040' },
                                }}>
                                {creatingInvite ? <CircularProgress size={22} /> : localize("연결 코드 생성")}
                            </Button>
                        </>
                    )
                ) : (
                    <>
                        <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mb: 2.5, textAlign: 'center' }}>{localize("다른 폰에서 만든 6자리 코드를 입력해주세요")}</SingleLineText>
                        <Box sx={{ bgcolor: '#f9fafb', borderRadius: 4, py: 2, px: 2, mb: 1.5, textAlign: 'center' }}>
                            <TextField fullWidth placeholder="000000" value={joinCode}
                                onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                inputProps={{ maxLength: 6, inputMode: 'numeric', style: { textAlign: 'center', fontSize: '2rem', letterSpacing: '0.3em', fontWeight: 800 } }}
                                variant="standard"
                                sx={{ '& .MuiInput-root': { '&::before': { borderBottom: 'none' }, '&::after': { borderBottom: 'none' }, '&:hover:not(.Mui-disabled)::before': { borderBottom: 'none' } } }}
                            />
                        </Box>
                        <SingleLineText sx={{ fontSize: '0.95rem', color: '#999', textAlign: 'center', mb: 2.5 }}>{localize("5분 내에 입력해야 해요")}</SingleLineText>
                        <Button variant="contained" fullWidth onMouseDown={(e) => e.preventDefault()}
                            onClick={handleJoin} disabled={joinCode.length !== 6 || joining}
                            sx={{
                                bgcolor: joinCode.length === 6 ? '#ffe566' : '#e5e7eb',
                                color: joinCode.length === 6 ? '#5a4a00' : '#888',
                                borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1.1rem',
                                '&:hover': { bgcolor: '#e8d040' },
                                '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888' },
                            }}>
                            {joining ? <CircularProgress size={24} sx={{ color: '#5a4a00' }} /> : localize("연결하기")}
                        </Button>
                    </>
                )}
            </BottomSheet>

            <Dialog
                open={disconnectConfirmOpen}
                onClose={() => { if (!disconnectingUuid) setDisconnectConfirmOpen(false); }}
                TransitionProps={{ onExited: () => setDisconnectTarget(null) }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>{localize("기기 연결을 끊을까요?")}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#777', fontSize: '0.95rem', lineHeight: 1.65 }}>
                        <strong style={{ color: '#1a1a1a' }}>{disconnectTarget ? getDeviceDisplayName(disconnectTarget) : ''}</strong>
                        {' '}{localize("기기가 현재 약 정보 그룹에서 분리돼요.")}{'\n'}{localize("분리된 기기는 새 빈 그룹으로 이동해요.")}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button
                        onClick={() => setDisconnectConfirmOpen(false)}
                        disabled={Boolean(disconnectingUuid)}
                        sx={{ color: '#999', fontWeight: 600 }}
                    >{localize("취소")}</Button>
                    <Button
                        onClick={async () => {
                            setDisconnectConfirmOpen(false);
                            await handleDisconnectDevice();
                        }}
                        disabled={Boolean(disconnectingUuid)}
                        sx={{
                            color: '#ef4444',
                            fontWeight: 700,
                        }}
                    >
                        {disconnectingUuid ? <CircularProgress size={18} sx={{ color: '#ef4444' }} /> : localize("연결 끊기")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 모든 약 알림 바꾸기 바텀시트 */}
            <BottomSheet open={alertMethodSheetOpen} onClose={() => { if (!savingAlertMethod) setAlertMethodSheetOpen(false); }}>
                <SettingsSheetTitle>
                    {t('모든 약 알림 바꾸기')}
                </SettingsSheetTitle>
                <SingleLineText sx={{ fontSize: '0.9rem', color: '#777', textAlign: 'center', lineHeight: 1.55, mb: 2.5, whiteSpace: readAppLanguage() === 'ko' ? 'nowrap' : 'normal', overflowWrap: readAppLanguage() === 'ko' ? undefined : 'anywhere' }}>
                    {t('완료·중지·필요시 약은 제외돼요')}
                </SingleLineText>
                <AlertMethodSection form={displayAlertMethodForm} setForm={setAlertMethodForm} />
                <Button variant="contained" fullWidth disabled={loadingAlertMethod || savingAlertMethod || medicationCount === 0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={saveAllMedicationAlertMethod}
                    sx={{
                        mt: 2.5,
                        bgcolor: medicationCount === 0 ? '#e5e7eb' : '#ffe566',
                        color: medicationCount === 0 ? '#888' : '#5a4a00',
                        borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1.1rem',
                        '&:hover': { bgcolor: medicationCount === 0 ? '#e5e7eb' : '#e8d040' },
                        '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888' },
                    }}>
                    {savingAlertMethod
                        ? <CircularProgress size={24} sx={{ color: '#5a4a00' }} />
                        : medicationCount === 0
                            ? t('변경할 알림 대상 약이 없어요')
                            : medicationCount == null
                                ? t('알림 대상 약에 적용하기')
                                : t('알림 대상 약 {count}개에 적용하기', { count: medicationCount })}
                </Button>
            </BottomSheet>

            {/* 복용 중지/재개 바텀시트 */}
            <BottomSheet open={pauseConfirmOpen} onClose={() => { if (!pauseSavingRef.current) setPauseConfirmOpen(false); }}>
                {globalPause ? (
                    <>
                        <SettingsSheetTitle>
                            {t('복용을 다시 시작할까요?')}
                        </SettingsSheetTitle>
                        <Typography sx={{ fontSize: '0.94rem', color: '#666', textAlign: 'center', lineHeight: 1.6, mb: 2.5 }}>
                            {t('전체 알림 중지를 해제하고')}<br />{t('정해진 시간에 다시 알려드려요')}
                        </Typography>
                        <Button variant="contained" fullWidth disabled={saving}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={async () => {
                                if (pauseSavingRef.current) return;
                                pauseSavingRef.current = true;
                                setSaving(true);
                                try {
                                    const uuid = getDeviceUuid();
                                    await updateSettings(uuid, { clearGlobalPause: true });
                                    clearAllScreenCaches();
                                    setGlobalPause(false); setPauseUntil(''); setPauseConfirmOpen(false);
                                    syncWidget(uuid);
                                    setSnackbar({ open: true, message: t('복용을 다시 시작했어요'), severity: 'success' });
                                } catch { setSnackbar({ open: true, message: t('저장 실패'), severity: 'error' }); }
                                finally { pauseSavingRef.current = false; setSaving(false); }
                            }}
                            sx={{
                                bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                                height: 52, py: 0, fontWeight: 700, fontSize: '1rem', boxShadow: 'none',
                                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                            }}>
                            {saving ? <CircularProgress size={24} sx={{ color: '#5a4a00' }} /> : t('복용 재개하기')}
                        </Button>
                    </>
                ) : (
                    <>
                        <SettingsSheetTitle>
                            {t('복용을 중지할까요?')}
                        </SettingsSheetTitle>
                        <Typography sx={{ fontSize: '0.94rem', color: '#666', textAlign: 'center', lineHeight: 1.6, mb: 2.5 }}>
                            {t('모든 약의 복용 알림을 잠시 쉬어요')}<br />{t('약 정보와 기록은 그대로 남아요')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <SingleLineText component="label" htmlFor="global-pause-until" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#444' }}>
                                {t('이 날짜까지')}
                            </SingleLineText>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={pauseIndefinitely} disabled={saving}
                                    onChange={(e) => setPauseIndefinitely(e.target.checked)}
                                    inputProps={{ 'aria-label': t('무기한 중지'), 'aria-describedby': 'global-pause-help' }}
                                    icon={<Box sx={{ width: 17, height: 17, border: '1.5px solid #c9cdd2', borderRadius: '50%', boxSizing: 'border-box' }} />}
                                    checkedIcon={<Box sx={{ width: 17, height: 17, borderRadius: '50%', bgcolor: '#ffe566', color: '#6b5700', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800 }}>✓</Box>}
                                    sx={{ p: 1, mr: -0.25 }} />}
                                label={t('무기한 중지')}
                                sx={{ m: 0, mr: -0.5, minHeight: 44, '& .MuiFormControlLabel-label': { fontSize: '0.82rem', fontWeight: 500, color: pauseIndefinitely ? '#6b5700' : '#888' } }}
                            />
                        </Box>
                        <Box sx={{ position: 'relative', height: 56, borderRadius: '14px', border: '1px solid #eceef1', bgcolor: '#f7f8fa' }}>
                            <Box sx={{ height: '100%', px: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pointerEvents: 'none' }}>
                                {pauseIndefinitely ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PauseCircleOutlineIcon aria-hidden="true" sx={{ fontSize: 20, color: '#8a7000' }} />
                                        <SingleLineText sx={{ fontSize: '0.94rem', fontWeight: 600, color: '#333' }}>{t('직접 다시 시작할 때까지')}</SingleLineText>
                                    </Box>
                                ) : (
                                    <>
                                        <Typography sx={{ fontSize: '0.96rem', fontWeight: pauseUntil ? 600 : 500, color: pauseUntil ? '#333' : '#777', whiteSpace: 'nowrap' }}>
                                            {pauseUntil ? (readAppLanguage() === 'ko' ? localize("{v0}년 {v1}월 {v2}일", { v0: pauseUntil.slice(0, 4), v1: Number(pauseUntil.slice(5, 7)), v2: Number(pauseUntil.slice(8, 10)) }) : pauseUntil) : t('날짜를 선택해 주세요')}
                                        </Typography>
                                        <CalendarMonthIcon aria-hidden="true" sx={{ fontSize: 22, color: '#8a7000', flexShrink: 0 }} />
                                    </>
                                )}
                            </Box>
                            <Box component="input" id="global-pause-until" type="date" value={pauseUntil}
                                min={localToday()} disabled={saving || pauseIndefinitely}
                                onChange={(e) => setPauseUntil(e.target.value)}
                                onClick={(e) => {
                                    try {
                                        if (typeof e.currentTarget.showPicker === 'function') e.currentTarget.showPicker();
                                    } catch (_) { e.currentTarget.focus(); }
                                }}
                                aria-describedby="global-pause-help"
                                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 0, fontSize: 16, visibility: pauseIndefinitely ? 'hidden' : 'visible' }}
                            />
                        </Box>
                        <Typography id="global-pause-help" sx={{ fontSize: '0.82rem', color: '#888', lineHeight: 1.5, mt: 1.25, mb: 2.5 }}>
                            {t(pauseIndefinitely ? '설정에서 언제든 다시 시작할 수 있어요.' : '선택한 날의 다음 날부터 다시 알려드려요.')}
                        </Typography>
                        <Button variant="contained" fullWidth disabled={(!pauseIndefinitely && !isPauseActive(pauseUntil)) || saving}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={async () => {
                                if (pauseSavingRef.current) return;
                                pauseSavingRef.current = true;
                                setSaving(true);
                                try {
                                    const uuid = getDeviceUuid();
                                    const until = pauseIndefinitely ? INDEFINITE_PAUSE_UNTIL : pauseUntil;
                                    await updateSettings(uuid, { globalPausedUntil: until });
                                    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'STOP_ALARM' }));
                                    clearAllScreenCaches();
                                    setGlobalPause(true); setPauseUntil(until); setPauseConfirmOpen(false);
                                    syncWidget(uuid);
                                    setSnackbar({ open: true, message: t('복용을 중지했어요'), severity: 'success' });
                                } catch { setSnackbar({ open: true, message: t('저장 실패'), severity: 'error' }); }
                                finally { pauseSavingRef.current = false; setSaving(false); }
                            }}
                            sx={{
                                bgcolor: '#ffe566', color: '#5a4a00',
                                borderRadius: 3, height: 52, py: 0, fontWeight: 700, fontSize: '1rem', boxShadow: 'none',
                                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888' },
                            }}>
                            {saving ? <CircularProgress size={22} sx={{ color: '#5a4a00' }} /> : t('복용 중지하기')}
                        </Button>
                    </>
                )}
            </BottomSheet>

            {/* 데이터 삭제 — 1차 확인 */}
            <Dialog open={deleteStep === 1} onClose={() => deleting ? null : setDeleteStep(0)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>
                    {t('내 데이터를 모두 삭제할까요?')}
                </DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '1rem', color: '#666', lineHeight: 1.55 }}>
                        {t('등록한 약, 복용 기록, 보호자 연결, 신고 내역까지 모두 사라져요. 같은 그룹에 연결된 다른 기기의 데이터도 함께 삭제됩니다.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteStep(0)} variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            bgcolor: '#f5f5f5',
                            '&:hover': { bgcolor: '#ececec' },
                        }}>
                        {t('취소')}
                    </Button>
                    <Button onClick={() => setDeleteStep(2)} variant="contained"
                        sx={{
                            bgcolor: '#ef4444', color: '#fff', fontWeight: 700,
                            fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                        }}>
                        {t('다음')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 데이터 삭제 — 2차 최종 확인 */}
            <Dialog open={deleteStep === 2} onClose={() => deleting ? null : setDeleteStep(0)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>
                    {t('되돌릴 수 없어요')}
                </DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '1rem', color: '#666', lineHeight: 1.55 }}>
                        {t('한 번 더 확인할게요. 정말로 모든 데이터를 영구 삭제할까요?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteStep(0)} disabled={deleting} variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            bgcolor: '#f5f5f5',
                            '&:hover': { bgcolor: '#ececec' },
                        }}>
                        {t('취소')}
                    </Button>
                    <Button onClick={handleDeleteAllData} disabled={deleting} variant="contained"
                        sx={{
                            bgcolor: '#ef4444', color: '#fff', fontWeight: 700,
                            fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' },
                        }}>
                        {deleting ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />{t('삭제 중...')}</> : t('삭제할게요')}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppToast open={snackbar.open} autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <ScrollDownIndicator />
        </Box>
    );
}
